import { Fragment, useMemo, useState, type MouseEvent, type ReactNode } from 'react';
import { Square, SquareCheck, WrapText } from 'lucide-react';
import { Link } from 'react-router-dom';
import { mentionsToPlainText, parseMentionTokens, taskContextUrl } from '@shared/task-contexts';
import { tagRanges } from '@shared/task-tags';
import type { TaskContext, TaskMember } from '@shared/types/tasks';
import { sourceRefId, sourceRefOfUrl, sourceTextRefs, type SourceRefScope } from '@shared/source-refs';
import { TASK_SOURCE_LABELS, type TaskSourceRef, type TaskSourceRefs } from '@shared/types/task-sources';
import { TrackerIcon } from '@/components/TrackerIcon';
import { useCurrentProject } from '@/lib/current-project';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { CopyTextButton } from '@/components/factory/ChatMessageParts';
import { memberHandle, memberName } from './task-ui';
import { ExternalLink } from '@/components/ExternalLink';
import { useAiBuilderMentions } from './ai-builder-mention';
import { useBoardPath } from '@/navigation/project-paths';
import { hasTaskRefs, taskRefRanges, useTaskRefPath, type TaskRefPath } from './task-ref-links';
import {
  SimpleTable,
  SimpleTableBody,
  SimpleTableCell,
  SimpleTableHead,
  SimpleTableHeader,
  SimpleTableRow,
} from '@/components/SimpleTable';
import { parseCommentMarkdown, pictureRuns, type MdAlign, type MdBlock, type MdInline } from './comment-markdown';
import { FactoryQuestionsCard, FactoryQuestionsPointer } from './FactoryQuestionsCard';
import { parseDecisionsCode } from '@shared/factory-suggestions';
import { FACTORY_BACKLOG_LANG, parseFactoryBacklogJson, type FactoryBacklogItem } from '@shared/factory-backlog';
import { FactorySuggestionsCard, FactorySuggestionsPointer } from './FactorySuggestionsCard';
import { FACTORY_DISPUTE_LANG, parseFactoryDisputeJson } from '@shared/factory-dispute';
import { FactoryDisputeCard, FactoryDisputePointer } from './FactoryDisputeCard';
import { FACTORY_SUBTASKS_LANG, parseFactorySubtasksJson } from '@shared/factory-subtasks';
import { FactorySubtasksCard, FactorySubtasksPointer } from './FactorySubtasksCard';
import { FACTORY_STEER_LANG, parseFactorySteerJson } from '@shared/factory-progress';
import { FactorySteerOptions, type SteerProps } from './FactorySteerOptions';
import { useLinkOpener, type LinkOpener } from '@/pages/link-dialog/use-link-opener';

type BoardPath = ReturnType<typeof useBoardPath>;

const URL_RE = /(https?:\/\/[^\s<>()]+[^\s<>().,;:!?'"])/g;

/**
 * A factory link's click: in the chat and task comments it opens the link dialog (SF-T-160) and stays
 * on the page; otherwise it's leaving (`onNavigate`, so a task pop-up closes).
 */
function internalLink(to: string, ctx: Pick<RunContext, 'opener' | 'onNavigate'>) {
  return {
    onClick: (event: MouseEvent<HTMLAnchorElement>) => {
      if (!ctx.opener.onInternalClick(to, event)) ctx.onNavigate?.();
    },
    // It opens over the page, so the page's unsaved-changes guard lets it through.
    'data-keeps-page': ctx.opener.keepsPage(to) ? '' : undefined,
  };
}

/** `#tags` in plain text link to the Board filtered by that tag. */
function tagify(text: string, keyPrefix: string, ctx: LinkContext): ReactNode[] {
  const { boardPath } = ctx;
  const parts: ReactNode[] = [];
  let cursor = 0;
  tagRanges(text).forEach((r, i) => {
    parts.push(text.slice(cursor, r.start));
    const to = boardPath(null, `?tag=${encodeURIComponent(r.tag)}`);
    parts.push(
      <Link
        key={`${keyPrefix}-tag${i}`}
        to={to}
        {...internalLink(to, ctx)}
        className="rounded-sm bg-muted px-0.5 text-foreground underline-offset-2 hover:underline"
      >
        {text.slice(r.start, r.end)}
      </Link>
    );
    cursor = r.end;
  });
  parts.push(text.slice(cursor));
  return parts;
}

/** Task refs with a project prefix (SF-T-12, T-25) link to their task; the text around them gets #tags. */
function refify(text: string, keyPrefix: string, ctx: LinkContext): ReactNode[] {
  const parts: ReactNode[] = [];
  let cursor = 0;
  taskRefRanges(text).forEach((r, i) => {
    const to = ctx.taskRefPath(r.prefix, r.number);
    if (!to) return;
    parts.push(...tagify(text.slice(cursor, r.start), `${keyPrefix}-r${i}`, ctx));
    parts.push(
      <Link key={`${keyPrefix}-ref${i}`} to={to} {...internalLink(to, ctx)} className="font-medium text-primary underline-offset-2 hover:underline">
        {text.slice(r.start, r.end)}
      </Link>
    );
    cursor = r.end;
  });
  parts.push(...tagify(text.slice(cursor), `${keyPrefix}-rend`, ctx));
  return parts;
}

type LinkContext = Pick<RunContext, 'boardPath' | 'onNavigate' | 'taskRefPath' | 'opener' | 'sourceRefs'>;

/**
 * A web address, opening in a new tab; in the chat and task comments, in the framed dialog where the
 * site allows it, and this site's own addresses in the link dialog (SF-T-160).
 */
function WebLink({ href, ctx, className, children, hint }: { href: string; ctx: LinkContext; className: string; children: ReactNode; hint?: string }) {
  const own = ctx.opener.ownPath(href);
  if (own) {
    return (
      <Link to={own} {...internalLink(own, ctx)} className={cn('text-primary underline-offset-2 hover:underline', className)} data-hint={hint}>
        {children}
      </Link>
    );
  }
  return (
    <ExternalLink href={href} icon={false} className={className} data-hint={hint} onClick={ctx.opener.active ? (event) => ctx.opener.onExternalClick(href, event) : undefined}>
      {children}
    </ExternalLink>
  );
}

/** SF-T-76: what a mirrored task's text names on its source, by `person:<key>` / `task:<key>`. */
interface SourceRefsContext {
  scope: SourceRefScope;
  byId: Map<string, TaskSourceRef>;
  /** This project's task prefix, for a named task that's mirrored here too. */
  prefix: string | null;
}

/**
 * SF-T-76: a person or task the source's text names, by its name or title with the source's mark: a person
 * links to their page there (when it has one), a task to itself there, or here when it's mirrored here too.
 * `href` is the link the text had, which it opens rather than the kept one; `children` a markdown link's own words, shown
 * instead of the name or title (which becomes the hint).
 */
function SourceRefChip({
  found,
  href,
  ctx,
  children,
}: {
  found: TaskSourceRef;
  href?: string;
  ctx: LinkContext;
  children?: ReactNode;
}) {
  const refs = ctx.sourceRefs!;
  const kind = refs.scope.kind;
  const source = TASK_SOURCE_LABELS[kind];
  const icon = <TrackerIcon kind={kind} className="mr-1 inline size-[0.95em] -translate-y-px align-[-0.1em]" />;
  const name = found.name;
  const mirrored = found?.task && refs.prefix ? ctx.taskRefPath(refs.prefix, found.task.number) : null;
  if (found?.type === 'task' && mirrored) {
    const local = `${refs.prefix}-T-${found.task!.number}`;
    return (
      <Link
        to={mirrored}
        {...internalLink(mirrored, ctx)}
        className="font-medium text-primary underline-offset-2 hover:underline"
        data-hint={`${local}${children ? ` ${found.task!.title}` : ''} here, from ${source}${found.ref ? ` ${found.ref}` : ''}`}
      >
        {icon}
        {children ?? `${local} ${found.task!.title}`}
      </Link>
    );
  }
  const text = children ? (
    <>
      {icon}
      {children}
    </>
  ) : found?.type === 'task' ? (
      <>
        {icon}
        {found.ref ? <span className="text-muted-foreground">{found.ref} </span> : null}
        {name}
      </>
    ) : (
      <>
        {icon}
        {name}
      </>
    );
  const hint =
    found?.type === 'task' ? `${source} task${found.ref ? ` ${found.ref}` : ''}${children ? `: ${name}` : ''}` : `${name} on ${source}`;
  const url = href ?? found?.url;
  if (!url) {
    return (
      <span className="font-medium text-primary" data-hint={hint}>
        {text}
      </span>
    );
  }
  return (
    <WebLink href={url} ctx={ctx} className="inline font-medium" hint={`Open in ${source}: ${hint}`}>
      {text}
    </WebLink>
  );
}

/** SF-T-76: the people and tasks the source's text names (`@sam`, `#12`, `T-424`) as chips; the rest goes on to refify. */
function sourcify(text: string, keyPrefix: string, ctx: LinkContext): ReactNode[] {
  const refs = ctx.sourceRefs;
  if (!refs) return refify(text, keyPrefix, ctx);
  const parts: ReactNode[] = [];
  let cursor = 0;
  sourceTextRefs(refs.scope, text).forEach((r, i) => {
    const found = refs.byId.get(sourceRefId(r));
    if (!found && !r.label) return;
    parts.push(...refify(text.slice(cursor, r.start), `${keyPrefix}-s${i}`, ctx));
    // A jflo mention the sync hasn't kept shows as written: @Name, no mark.
    parts.push(found ? <SourceRefChip key={`${keyPrefix}-src${i}`} found={found} ctx={ctx} /> : `@${r.label}`);
    cursor = r.end;
  });
  parts.push(...refify(text.slice(cursor), `${keyPrefix}-send`, ctx));
  return parts;
}

function linkify(text: string, keyPrefix: string, ctx: LinkContext): ReactNode[] {
  return text.split(URL_RE).map((part, i) => {
    if (i % 2 === 0) return <Fragment key={`${keyPrefix}-${i}`}>{sourcify(part, `${keyPrefix}-${i}`, ctx)}</Fragment>;
    const ref = ctx.sourceRefs ? sourceRefOfUrl(ctx.sourceRefs.scope, part) : null;
    const found = ref ? ctx.sourceRefs!.byId.get(sourceRefId(ref)) : undefined;
    if (found) return <SourceRefChip key={`${keyPrefix}-${i}`} found={found} href={part} ctx={ctx} />;
    return (
      <WebLink href={part} key={`${keyPrefix}-${i}`} ctx={ctx} className="inline break-all">
        {part}
      </WebLink>
    );
  });
}

interface RunContext {
  members: TaskMember[];
  contexts: TaskContext[];
  aiOn: boolean;
  boardPath: BoardPath;
  taskRefPath: TaskRefPath;
  onNavigate?: () => void;
  opener: LinkOpener;
  questions?: QuestionsProps;
  onShowSuggestions?: () => void;
  onShowSubtasks?: () => void;
  onShowDecisions?: () => void;
  steer?: SteerProps;
  sourceRefs: SourceRefsContext | null;
  renderImage?: (src: string, alt: string, link: ReactNode) => ReactNode;
  renderImages?: (images: { src: string; alt: string }[], blocks: ReactNode) => ReactNode;
}

/** The AI builder's questions in a message (T-10): whether a person has replied since, and how to answer. */
export interface QuestionsProps {
  answered: boolean;
  /** SF-T-279: closed unanswered (a person closed the run that asked). */
  closed?: boolean;
  onAnswer?: (message: string) => void;
  /** The questions live elsewhere (the task's Decisions, T-22): show one line that goes there instead of the card. */
  onShow?: () => void;
}

/** One run of stored text: people mentions in bold, thing mentions as links, #tags and web addresses linked. */
function renderRun(text: string, keyPrefix: string, ctx: RunContext): ReactNode[] {
  const { members, contexts, aiOn } = ctx;
  const tokens = parseMentionTokens(text);
  const parts: ReactNode[] = [];
  let cursor = 0;
  tokens.forEach((token, i) => {
    parts.push(...linkify(text.slice(cursor, token.start), `${keyPrefix}t${i}`, ctx));
    const key = `${keyPrefix}m${i}`;
    if (token.target.kind === 'user') {
      const id = token.target.id;
      const member = members.find((m) => m.user_id === id);
      // SF-T-76: on a jflo task, a mention that's no one here is a jflo person (jflo writes them as the factory does).
      const jfloPerson = !member && ctx.sourceRefs?.scope.kind === 'jflo' ? ctx.sourceRefs.byId.get(sourceRefId({ type: 'person', key: id })) : undefined;
      if (jfloPerson) {
        parts.push(<SourceRefChip key={key} found={jfloPerson} ctx={ctx} />);
        cursor = token.end;
        return;
      }
      parts.push(
        <span key={key} className="font-medium text-primary" data-hint={member ? memberName(member) : undefined}>
          @{member ? memberHandle(member) : token.label}
        </span>
      );
    } else if (token.target.kind === 'ai') {
      parts.push(
        aiOn ? (
          <span key={key} className="font-medium text-primary" data-hint="The task loop">
            @{token.label}
          </span>
        ) : (
          `@${token.label}`
        )
      );
    } else {
      const { ref } = token.target;
      const known = contexts.find((c) => c.type === ref.type && c.id === ref.id);
      const to = known?.url ?? taskContextUrl(ref);
      parts.push(
        <Link
          key={key}
          to={to}
          {...internalLink(to, ctx)}
          className="font-medium text-primary underline-offset-2 hover:underline"
        >
          @{token.label}
        </Link>
      );
    }
    cursor = token.end;
  });
  parts.push(...linkify(text.slice(cursor), `${keyPrefix}end`, ctx));
  return parts;
}

/** Inline markdown. Inside a link (`plain`), mentions and addresses are text: links don't nest. */
function renderInline(nodes: MdInline[], keyPrefix: string, ctx: RunContext, plain = false): ReactNode[] {
  return nodes.map((node, i) => {
    const key = `${keyPrefix}-${i}`;
    switch (node.type) {
      case 'text':
        return plain ? (
          <Fragment key={key}>{mentionsToPlainText(node.text)}</Fragment>
        ) : (
          <Fragment key={key}>{renderRun(node.text, key, ctx)}</Fragment>
        );
      case 'code':
        return (
          <code key={key} className="rounded-sm bg-muted px-1 py-0.5 font-mono text-[0.9em]">
            {node.text}
          </code>
        );
      case 'strong':
        return (
          <strong key={key} className="font-semibold">
            {renderInline(node.children, key, ctx, plain)}
          </strong>
        );
      case 'em':
        return <em key={key}>{renderInline(node.children, key, ctx, plain)}</em>;
      case 'del':
        return (
          <del key={key} className="text-muted-foreground">
            {renderInline(node.children, key, ctx, plain)}
          </del>
        );
      case 'image': {
        // SF-T-266: a picture where the page can show it (the agent's messages), else a link to it named by its words.
        const link = renderInline([{ type: 'link', href: node.src, children: [{ type: 'text', text: node.alt || node.src }] }], key, ctx, plain);
        return <Fragment key={key}>{plain || !ctx.renderImage ? link : ctx.renderImage(node.src, node.alt, link)}</Fragment>;
      }
      case 'link': {
        // SF-T-76: a link to one of the source's people or tasks keeps its words, with the source's mark.
        const ref = !plain && ctx.sourceRefs ? sourceRefOfUrl(ctx.sourceRefs.scope, node.href) : null;
        const found = ref ? ctx.sourceRefs!.byId.get(sourceRefId(ref)) : undefined;
        if (found) {
          return (
            <SourceRefChip key={key} found={found} href={node.href} ctx={ctx}>
              {renderInline(node.children, key, ctx, true)}
            </SourceRefChip>
          );
        }
        return node.href.startsWith('/') ? (
          <Link key={key} to={node.href} {...internalLink(node.href, ctx)} className="text-primary underline-offset-2 hover:underline">
            {renderInline(node.children, key, ctx, true)}
          </Link>
        ) : (
          <WebLink key={key} href={node.href} ctx={ctx} className="inline break-words">
            {renderInline(node.children, key, ctx, true)}
          </WebLink>
        );
      }
    }
  });
}

const HEADING_CLASS = ['text-base font-semibold', 'text-base font-semibold', 'font-semibold', 'font-semibold', 'font-medium', 'font-medium'];
const ALIGN_CLASS: Record<NonNullable<MdAlign>, string> = { left: 'text-left', center: 'text-center', right: 'text-right' };

function renderBlocks(blocks: MdBlock[], keyPrefix: string, ctx: RunContext): ReactNode[] {
  if (!ctx.renderImages) return blocks.map((block, i) => renderBlock(block, `${keyPrefix}-${i}`, ctx));
  // SF-T-266: several pictures in a row (paragraphs of pictures only) show together, as a collage.
  return pictureRuns(blocks).map((run) => {
    const here = blocks.slice(run.from, run.to + 1).map((block, j) => renderBlock(block, `${keyPrefix}-${run.from + j}`, ctx));
    return run.pictures ? <Fragment key={`${keyPrefix}-${run.from}`}>{ctx.renderImages!(run.pictures, here)}</Fragment> : here[0];
  });
}

function renderBlock(block: MdBlock, key: string, ctx: RunContext): ReactNode {
  switch (block.type) {
    case 'paragraph':
      return (
        <p key={key} className="whitespace-pre-wrap">
          {renderInline(block.content, key, ctx)}
        </p>
      );
    case 'heading': {
      // Comments sit under the task's own headings, so `#` starts at h3.
      const Tag = `h${Math.min(block.level + 2, 6)}` as 'h3' | 'h4' | 'h5' | 'h6';
      return (
        <Tag key={key} className={cn('leading-snug', HEADING_CLASS[block.level - 1])}>
          {renderInline(block.content, key, ctx)}
        </Tag>
      );
    }
    case 'list': {
      const items = block.items.map((item, j) => (
        <li key={`${key}-${j}`} className={cn('space-y-1', item.checked != null && 'flex list-none items-start gap-1.5')}>
          {item.checked != null ? (
            <>
              {item.checked ? (
                <SquareCheck className="mt-[0.2em] size-[1em] shrink-0 text-primary" aria-label="Done" />
              ) : (
                <Square className="mt-[0.2em] size-[1em] shrink-0 text-muted-foreground" aria-label="Not done" />
              )}
              <div className="min-w-0 space-y-1">{renderBlocks(item.blocks, `${key}-${j}`, ctx)}</div>
            </>
          ) : (
            renderBlocks(item.blocks, `${key}-${j}`, ctx)
          )}
        </li>
      ));
      const tasks = block.items.every((item) => item.checked != null);
      return block.ordered ? (
        <ol key={key} start={block.start} className="list-decimal space-y-1 pl-5 marker:text-muted-foreground">
          {items}
        </ol>
      ) : (
        <ul key={key} className={cn('space-y-1 marker:text-muted-foreground', tasks ? 'pl-0.5' : 'list-disc pl-5')}>
          {items}
        </ul>
      );
    }
    case 'quote':
      return (
        <blockquote key={key} className="space-y-2 border-l-2 pl-3 text-muted-foreground">
          {renderBlocks(block.blocks, key, ctx)}
        </blockquote>
      );
    case 'code': {
      // SF-T-308: a decisions block shows as questions (grill) or suggestions (wrap); the old blocks as before.
      const decisions = parseDecisionsCode(block.lang ?? '', block.text);
      const questions = decisions && 'questions' in decisions ? decisions.questions : null;
      if (questions && ctx.questions?.onShow) {
        return <FactoryQuestionsPointer key={key} count={questions.questions.length} answered={ctx.questions.answered} closed={ctx.questions.closed} onShow={ctx.questions.onShow} />;
      }
      if (questions) {
        return <FactoryQuestionsCard key={key} questions={questions} answered={ctx.questions?.answered} closed={ctx.questions?.closed} onAnswer={ctx.questions?.onAnswer} />;
      }
      // T-14: the AI builder's suggestions are decided in the task's Suggestions; elsewhere they show as a card.
      const suggestions = decisions && 'suggestions' in decisions ? decisions.suggestions : null;
      if (suggestions && ctx.onShowSuggestions) {
        return <FactorySuggestionsPointer key={key} count={suggestions.suggestions.length} onShow={ctx.onShowSuggestions} />;
      }
      if (suggestions) return <FactorySuggestionsCard key={key} suggestions={suggestions} />;
      // SF-T-308: the AI builder's report on the Backlog picks it was handed; the decisions show it too.
      const backlog = block.lang === FACTORY_BACKLOG_LANG ? parseFactoryBacklogJson(block.text) : null;
      if (backlog) return <BacklogReport key={key} items={backlog} />;
      // SF-T-74: a disputed review's findings are settled in the task's Decisions; elsewhere they show as a card.
      const dispute = block.lang === FACTORY_DISPUTE_LANG ? parseFactoryDisputeJson(block.text) : null;
      if (dispute && ctx.onShowDecisions) return <FactoryDisputePointer key={key} count={dispute.findings.length} unclear={dispute.unclear} onShow={ctx.onShowDecisions} />;
      if (dispute) return <FactoryDisputeCard key={key} dispute={dispute} />;
      // Its proposed sub-tasks are added in the task's Proposed sub-tasks; elsewhere they show as a card.
      const subtasks = block.lang === FACTORY_SUBTASKS_LANG ? parseFactorySubtasksJson(block.text) : null;
      if (subtasks && ctx.onShowSubtasks) return <FactorySubtasksPointer key={key} count={subtasks.subtasks.length} onShow={ctx.onShowSubtasks} />;
      if (subtasks) return <FactorySubtasksCard key={key} subtasks={subtasks} />;
      // T-44: the steering suggestions on a progress report from the coding agent.
      const steerOptions = block.lang === FACTORY_STEER_LANG ? parseFactorySteerJson(block.text) : null;
      if (steerOptions) return <FactorySteerOptions key={key} options={steerOptions} steer={ctx.steer} />;
      return <CodeBlock key={key} lang={block.lang} text={mentionsToPlainText(block.text)} />;
    }
    case 'rule':
      return <hr key={key} className="border-border" />;
    case 'table':
      return (
        <SimpleTable key={key} size="compact">
          <SimpleTableHeader>
            <SimpleTableRow>
              {block.header.map((cell, c) => (
                <SimpleTableHead key={c} className={cn(block.align[c] && ALIGN_CLASS[block.align[c]!])}>
                  {renderInline(cell, `${key}-h${c}`, ctx)}
                </SimpleTableHead>
              ))}
            </SimpleTableRow>
          </SimpleTableHeader>
          <SimpleTableBody>
            {block.rows.map((row, r) => (
              <SimpleTableRow key={r}>
                {row.map((cell, c) => (
                  <SimpleTableCell key={c} className={cn(block.align[c] && ALIGN_CLASS[block.align[c]!])}>
                    {renderInline(cell, `${key}-${r}-${c}`, ctx)}
                  </SimpleTableCell>
                ))}
              </SimpleTableRow>
            ))}
          </SimpleTableBody>
        </SimpleTable>
      );
  }
}

/**
 * Renders stored text: people mentions in bold, thing mentions as links to their page, #tags as Board filters,
 * and task refs with a project prefix (SF-T-12) as links to the task.
 * With `markdown` (task comments, T-8; descriptions and the chat composer's Preview, T-9) it also renders markdown: headings, lists, quotes, code, tables, emphasis, links.
 */
export function MentionText({
  text,
  members,
  contexts = [],
  className,
  onNavigate,
  aiBuilder,
  markdown = false,
  questions,
  onShowSuggestions,
  onShowSubtasks,
  onShowDecisions,
  steer,
  sourceRefs,
  renderImage,
  renderImages,
}: {
  text: string;
  members: TaskMember[];
  /** The task's Contexts: their stored URLs are exact. */
  contexts?: TaskContext[];
  className?: string;
  onNavigate?: () => void;
  /** The project has the AI builder; else its mention is plain text. Defaults to the task's (AiBuilderMentions). */
  aiBuilder?: boolean;
  /** Render markdown (comments, descriptions). Off, the text shows as written, line breaks kept. */
  markdown?: boolean;
  /** With markdown: a `factory-questions` block shows as a question card (T-10), answerable through this. */
  questions?: QuestionsProps;
  /** With markdown: a `factory-suggestions` block (T-14) shows as one line that calls this (the task's Suggestions); without it, as a card. */
  onShowSuggestions?: () => void;
  /** With markdown: a `factory-subtasks` block shows as one line that calls this (the task's Proposed sub-tasks); without it, as a card. */
  onShowSubtasks?: () => void;
  /** With markdown: a `factory-dispute` block (SF-T-74) shows as one line that calls this (the task's Decisions); without it, as a card. */
  onShowDecisions?: () => void;
  /** With markdown: a `factory-steer` block (T-44) shows its options as buttons that steer the run, while it works. */
  steer?: SteerProps;
  /**
   * SF-T-76: the text is a mirrored task's source's (its description, a synced-in comment): the people and
   * tasks it names there show by name and title with the source's mark.
   */
  sourceRefs?: TaskSourceRefs | null;
  /** SF-T-266: with markdown, how `![alt](src)` shows (AgentPicture in the agent's messages); absent, as `link`, a link to it. */
  renderImage?: (src: string, alt: string, link: ReactNode) => ReactNode;
  /** SF-T-266: with markdown, several pictures in a row (paragraphs of pictures only) together; absent, each on its own. `blocks` is them as they'd show apart. */
  renderImages?: (images: { src: string; alt: string }[], blocks: ReactNode) => ReactNode;
}) {
  const aiOn = useAiBuilderMentions(aiBuilder);
  const boardPath = useBoardPath();
  const taskRefPath = useTaskRefPath(hasTaskRefs(text));
  const opener = useLinkOpener();
  const project = useCurrentProject();
  const prefix = project?.task_prefix ?? null;
  const source = useMemo(
    () => (sourceRefs ? { scope: { kind: sourceRefs.kind, repo: sourceRefs.repo, url: sourceRefs.url }, byId: new Map(sourceRefs.refs.map((r) => [sourceRefId(r), r])), prefix } : null),
    [sourceRefs, prefix]
  );
  const ctx: RunContext = { members, contexts, aiOn, boardPath, taskRefPath, onNavigate, opener, questions, onShowSuggestions, onShowSubtasks, onShowDecisions, steer, sourceRefs: source, renderImage, renderImages };
  if (markdown) {
    return <div className={cn('min-w-0 space-y-2 break-words', className)}>{renderBlocks(parseCommentMarkdown(text), 'b', ctx)}</div>;
  }
  return <div className={cn('whitespace-pre-wrap break-words', className)}>{renderRun(text, '', ctx)}</div>;
}

/**
 * SF-T-266: a code block, as T3 Code's: its language at the top left, and Wrap and Copy at the top right (on hover,
 * or always on a touch screen).
 */
function CodeBlock({ lang, text }: { lang: string; text: string }) {
  const [wrap, setWrap] = useState(false);
  return (
    <div className="group/code relative">
      <pre className={cn('overflow-x-auto rounded-md bg-muted px-3 py-2 pt-7 font-mono text-xs leading-relaxed', wrap && 'break-words whitespace-pre-wrap')}>
        {lang ? <span className="absolute top-2 left-3 font-sans text-3xs text-muted-foreground">{lang}</span> : null}
        <code>{text}</code>
      </pre>
      <div className="absolute top-1 right-1 flex items-center gap-0.5 opacity-0 transition-opacity group-hover/code:opacity-100 focus-within:opacity-100 pointer-coarse:opacity-100">
        <Button
          type="button"
          variant="ghost"
          size="icon-xs"
          className={cn('size-6 text-muted-foreground', wrap && 'text-foreground')}
          aria-label={wrap ? 'Stop wrapping lines' : 'Wrap long lines'}
          aria-pressed={wrap}
          onClick={() => setWrap(!wrap)}
        >
          <WrapText />
        </Button>
        <CopyTextButton text={text} label="Copy the code" />
      </div>
    </div>
  );
}

/** SF-T-308: what the AI builder did with each Backlog pick (a factory-backlog block): the task it made, or why none. */
function BacklogReport({ items }: { items: FactoryBacklogItem[] }) {
  return (
    <div className="space-y-1 rounded-md border bg-muted/40 px-2.5 py-1.5 text-xs text-muted-foreground">
      <p className="font-medium text-foreground">Backlog picks</p>
      <ul className="list-disc space-y-0.5 pl-4">
        {items.map((item) => (
          <li key={`${item.run ?? ''}-${item.number}`}>
            <span className="text-foreground">{item.title ?? `Suggestion ${item.number}`}</span>
            {item.task ? ` → made ${item.task}` : ` → no task: ${item.reason}`}
          </li>
        ))}
      </ul>
    </div>
  );
}
